import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import type { ViewMode } from "./useConfigList";

/**
 * SIMS-8602 — the four list states for a configuration screen.
 *
 * Spec — `Payers Pattern v2.dc.html`:
 *   load failed L138-150: 1px --error-main border, `cloud_off` in --error-dark,
 *                         title + body + primary "Try again"
 *   skeleton    L152-176 (cards, 6 placeholders) and L178-198 (table, 8 rows)
 *   no results  L200-212: `search_off`, names the term, "Clear search" + "Clear all filters"
 *   empty       L214-226: `account_balance`, first-run message + primary add action
 *
 * All four share one frame (--surface-0, 1px border, 12px radius) with the content centred, so
 * the states differ in what they SAY rather than in how they sit on the page — which is what
 * makes AC3's "visibly different" a matter of copy and icon, not layout.
 *
 * Copy is injected rather than hardcoded: the Actions and Cups & Analytes screens reuse this
 * component with their own nouns (hand-off §2 — the states are part of the shared pattern).
 *
 * The load-failure state is DESIGNED but has no AC on SIMS-8602 (three ACs, four states). AC4 was
 * proposed to the PO in comment 65582; TestRail C17588 already tests it and C17593 refers to "the
 * four list states". Built regardless — the design is unambiguous.
 */

export type ListState =
	| "ready"
	| "loading"
	| "load-failed"
	| "no-results"
	| "empty";

export interface ConfigListStatesCopy {
	loadFailedTitle: string;
	loadFailedBody: string;
	emptyTitle: string;
	emptyBody: string;
	/** Reserved: the first-run glyph. Currently the payer noun for every config screen. */
	emptyIcon?: string;
	/** Header row text for the loading table, e.g. "Loading payers…" (design L180). */
	loadingLabel: string;
	noResultsBody: string;
	noResultsTitle: (query: string) => string;
	addLabel: string;
}

export interface ConfigListStatesProps {
	state: ListState;
	view: ViewMode;
	copy: ConfigListStatesCopy;
	/** The search term, for the no-results title. Empty when only filters narrowed the list. */
	query?: string;
	onRetry?: () => void;
	onClearSearch?: () => void;
	onClearAllFilters?: () => void;
	onAdd?: () => void;
	/**
	 * The underlying failure, for logging only — deliberately NOT rendered. The previous screen
	 * printed `Error loading payers: {message}`, putting a raw API message (paths, status codes)
	 * on the screen. The designed copy is generic on purpose.
	 */
	errorMessage?: string;
}

/**
 * The state containers are `flex: 1` in the design (L140, L202, L216): the box takes the height
 * left on the page and the content centres inside it via `margin: auto` (L141). A fixed
 * min-height, which this used at first, leaves the frame floating short of the page bottom —
 * reported from the running screen.
 *
 * `flexGrow` needs a flex parent; the page column supplies one (L87: the list body is
 * `flex: 1; display: flex; flex-direction: column; min-height: 0`). `minHeight: 0` here lets it
 * shrink below content height rather than overflowing.
 */
const FRAME = {
	flex: 1,
	minHeight: 0,
	backgroundColor: "background.paper",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: "0.75rem",
	padding: 6,
	display: "flex",
} as const;

const CENTRED = {
	// `margin: auto` inside a flex parent centres on BOTH axes (design L141).
	margin: "auto",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	textAlign: "center",
	gap: 1,
	maxWidth: "24rem",
} as const;

const ConfigListStates = ({
	state,
	view,
	copy,
	query = "",
	onRetry,
	onClearSearch,
	onClearAllFilters,
	onAdd,
}: ConfigListStatesProps) => {
	if (state === "ready") {
		return null;
	}

	if (state === "loading") {
		// AC1: the placeholder appears in whichever view is active, so there are two skeletons.
		// Both sit INSIDE the frame, and the table variant keeps a header row reading
		// "Loading <records>…" on the header tint (design L179-181) so the frame does not
		// change shape when the real table arrives.
		return view === "cards" ? (
			<Box
				data-testid="config-list-skeleton-cards"
				sx={{
					flex: 1,
					minHeight: 0,
					backgroundColor: "background.paper",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: "0.75rem",
					overflow: "hidden",
					padding: 2,
					display: "grid",
					gridTemplateColumns: "repeat(auto-fill, minmax(17.5rem, 1fr))",
					gap: 2,
					alignContent: "start",
				}}
			>
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton key={i} variant="rounded" height="7.5rem" />
				))}
			</Box>
		) : (
			<Box
				data-testid="config-list-skeleton-table"
				sx={{
					flex: 1,
					minHeight: 0,
					backgroundColor: "background.paper",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: "0.75rem",
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						height: "3rem",
						backgroundColor: (theme) => theme.palette.surfaceTint.main,
						borderBottom: "1px solid",
						borderBottomColor: "divider",
						display: "flex",
						alignItems: "center",
						padding: "0 1rem",
						fontSize: "0.75rem",
						fontWeight: 600,
						letterSpacing: "0.04em",
						textTransform: "uppercase",
						color: "text.secondary",
					}}
				>
					{copy.loadingLabel}
				</Box>
				{Array.from({ length: 8 }).map((_, i) => (
					<Skeleton
						key={i}
						variant="rectangular"
						height="3.5rem"
						sx={{ mb: "1px" }}
					/>
				))}
			</Box>
		);
	}

	if (state === "load-failed") {
		return (
			<Box
				sx={{ ...FRAME, borderColor: "error.main" }}
				data-testid="config-list-load-failed"
			>
				<Box sx={CENTRED}>
					<CloudOffIcon sx={{ fontSize: "2.5rem", color: "error.dark" }} />
					<Typography variant="h6">{copy.loadFailedTitle}</Typography>
					<Typography variant="body2" color="text.secondary">
						{copy.loadFailedBody}
					</Typography>
					{onRetry && (
						<Button
							variant="contained"
							size="small"
							startIcon={<RefreshIcon />}
							onClick={onRetry}
							sx={{ mt: 1 }}
						>
							Try again
						</Button>
					)}
				</Box>
			</Box>
		);
	}

	if (state === "no-results") {
		return (
			<Box sx={FRAME} data-testid="config-list-no-results">
				<Box sx={CENTRED}>
					<SearchOffIcon sx={{ fontSize: "2.5rem", color: "text.secondary" }} />
					<Typography variant="h6">{copy.noResultsTitle(query)}</Typography>
					<Typography variant="body2" color="text.secondary">
						{copy.noResultsBody}
					</Typography>
					<Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
						{/* Only offered when a term was actually typed: a filter-only narrowing has no
						    search to clear (TestRail C17590). */}
						{query !== "" && onClearSearch && (
							<Button variant="outlined" size="small" onClick={onClearSearch}>
								Clear search
							</Button>
						)}
						{onClearAllFilters && (
							<Button variant="text" size="small" onClick={onClearAllFilters}>
								Clear all filters
							</Button>
						)}
					</Box>
				</Box>
			</Box>
		);
	}

	// state === "empty" — the first run, deliberately unlike no-results: a different icon, a
	// different title, an explanation of what the records are for, and an add action instead of
	// an escape hatch (AC3, hand-off §7 check 1).
	return (
		<Box sx={FRAME} data-testid="config-list-empty">
			<Box sx={CENTRED}>
				<AccountBalanceIcon
					sx={{ fontSize: "2.5rem", color: "text.secondary" }}
				/>
				<Typography variant="h6">{copy.emptyTitle}</Typography>
				<Typography variant="body2" color="text.secondary">
					{copy.emptyBody}
				</Typography>
				{onAdd && (
					<Button
						variant="contained"
						size="small"
						startIcon={<AddIcon />}
						onClick={onAdd}
						sx={{ mt: 1 }}
					>
						{copy.addLabel}
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default ConfigListStates;
